/**
 * Maya — Premium audio tour guide card.
 *
 * Renders above the booking section on tour pages and offers Maya's narrated
 * cultural mini-guide of the destination. Topics are mapped to the cached
 * destination guide; narration is generated server-side via /api/maya-tts.
 *
 * SECURITY: `/api/maya-tts` performs its own server-side `has_maya_access`
 * check on every request. The client `unlocked` flag is a UX gate only —
 * if a 403 comes back, we clear the local unlock state and reopen the
 * paywall so the user knows their access is not valid.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { MayaMark } from "@/components/site/BrandLockup";
import { usePublishedGuide, FIRST_CHAPTER_FREE } from "@/lib/published-guide";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Download,
  Loader2,
  Pause,
  Play,
  Square,
  Sparkles,
} from "lucide-react";

import { detectTourPlaces } from "@/lib/tour-places";
import {
  getDestinationGuide,
  type DestinationGuide,
  type GuideSectionKey,
} from "@/lib/destination-guide.functions";
import { normalizeLang } from "@/i18n";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { getMyMayaAccess, getGuestMayaAccess } from "@/lib/maya-access.functions";
import { getSupabase } from "@/lib/supabase-lazy";
import { getMayaAnonSessionId } from "@/lib/maya-session";


import { MayaPaywall } from "@/components/maya/MayaPaywall";
import { MayaAudioPreview } from "@/components/maya/MayaAudioPreview";
import { getRememberedMayaEmail } from "@/lib/maya-guest";

type SupportedLang = "es" | "en" | "fr" | "pt" | "it";

interface Props {
  title: string;
  location: string | null;
  summary: string | null;
  /** Slug of the tour this guide belongs to (enables per-tour access). */
  tourSlug?: string | null;
  /** Premium = bigger hero card; compact = a slim panel. */
  variant?: "premium" | "compact";
  /** Itinerario real del tour: la guía se arma por lugar visitado. */
  itinerary?: { title: string; body: string | null }[];
}

interface TopicDef {
  id: string;
  emoji: string;
  labelKey: keyof typeof TOPIC_LABELS["es"];
  /** Section keys (from the guide) we should narrate for this topic. */
  sectionKeys: GuideSectionKey[];
  /** Also include the "did you know" bullets. */
  includeDidYouKnow?: boolean;
  /** Generic travel tips — produced from the guide intro + location. */
  travelTips?: boolean;
}

const TOPICS: TopicDef[] = [
  { id: "history",        emoji: "📜", labelKey: "history",     sectionKeys: ["history"] },
  { id: "fauna_flora",    emoji: "🦜", labelKey: "faunaFlora",  sectionKeys: ["fauna_flora"] },
  { id: "geography",      emoji: "🗺️", labelKey: "geography",   sectionKeys: ["geography"] },
  { id: "culture",        emoji: "🎭", labelKey: "culture",     sectionKeys: ["culture", "traditions", "customs"] },
  { id: "gastronomy",     emoji: "🌮", labelKey: "gastronomy",  sectionKeys: ["gastronomy"] },
  { id: "archaeology",    emoji: "🏛️", labelKey: "archaeology", sectionKeys: ["history", "culture"] },
  { id: "legends",        emoji: "✨", labelKey: "legends",     sectionKeys: ["legends"], includeDidYouKnow: true },
  { id: "tips",           emoji: "💡", labelKey: "tips",        sectionKeys: [], travelTips: true },
];

const TOPIC_LABELS = {
  es: {
    history: "Historia", faunaFlora: "Flora y Fauna", geography: "Geografía",
    culture: "Cultura y Tradiciones", gastronomy: "Gastronomía",
    archaeology: "Arqueología y Patrimonio", legends: "Leyendas y Curiosidades",
    tips: "Consejos de Viaje", listen: "Escuchar", listenAll: "Escuchar Todo",
    sectionTitle: "Escucha con Maya",
    premiumTitle: "Descubre este destino con Maya",
    welcome: "Hola, soy Maya. ¿Te gustaría conocer la historia, cultura, gastronomía y curiosidades de este destino antes de reservar?",
    pause: "Pausar", resume: "Reanudar", stop: "Detener", play: "Reproducir",
    loading: "Maya está preparando la narración…",
    fallback: "Aún no tengo información narrada para este destino, pero pronto la tendré lista.",
    error: "Maya no pudo narrar ahora mismo. Inténtalo de nuevo en unos segundos.",
    tooMany: "Demasiadas solicitudes. Espera un momento antes de intentarlo de nuevo.",
    accessRequired: "Necesitas acceso Maya Premium para escuchar. Desbloquéalo abajo.",
    nowPlaying: "Reproduciendo",
    selectTopic: "Elige un tema y déjate guiar por la voz de Maya.",
  },
  en: {
    history: "History", faunaFlora: "Flora & Fauna", geography: "Geography",
    culture: "Culture & Traditions", gastronomy: "Gastronomy",
    archaeology: "Archaeology & Heritage", legends: "Legends & Curiosities",
    tips: "Travel Tips", listen: "Listen", listenAll: "Listen to all",
    sectionTitle: "Listen with Maya",
    premiumTitle: "Discover this destination with Maya",
    welcome: "Hi, I'm Maya. Would you like to hear the history, culture, food and curiosities of this destination before you book?",
    pause: "Pause", resume: "Resume", stop: "Stop", play: "Play",
    loading: "Maya is preparing the narration…",
    fallback: "I don't have narrated info for this destination yet, but I'll have it ready soon.",
    error: "Maya couldn't narrate just now. Please try again in a few seconds.",
    tooMany: "Too many requests. Please wait a moment and try again.",
    accessRequired: "You need Maya Premium access to listen. Unlock it below.",
    nowPlaying: "Now playing",
    selectTopic: "Pick a topic and let Maya guide you.",
  },
  fr: {
    history: "Histoire", faunaFlora: "Flore et Faune", geography: "Géographie",
    culture: "Culture et Traditions", gastronomy: "Gastronomie",
    archaeology: "Archéologie et Patrimoine", legends: "Légendes et Curiosités",
    tips: "Conseils de Voyage", listen: "Écouter", listenAll: "Tout écouter",
    sectionTitle: "Écoute avec Maya",
    premiumTitle: "Découvre cette destination avec Maya",
    welcome: "Bonjour, je suis Maya. Veux-tu connaître l'histoire, la culture, la gastronomie et les curiosités de cette destination avant de réserver ?",
    pause: "Pause", resume: "Reprendre", stop: "Arrêter", play: "Lire",
    loading: "Maya prépare la narration…",
    fallback: "Je n'ai pas encore de narration pour cette destination, je l'aurai bientôt.",
    error: "Maya n'a pas pu narrer maintenant. Réessaie dans quelques secondes.",
    tooMany: "Trop de requêtes. Patiente un instant et réessaie.",
    accessRequired: "L'accès Maya Premium est requis pour écouter. Débloque-le ci-dessous.",
    nowPlaying: "Lecture en cours",
    selectTopic: "Choisis un thème et laisse-toi guider par la voix de Maya.",
  },
  pt: {
    history: "História", faunaFlora: "Flora e Fauna", geography: "Geografia",
    culture: "Cultura e Tradições", gastronomy: "Gastronomia",
    archaeology: "Arqueologia e Patrimônio", legends: "Lendas e Curiosidades",
    tips: "Dicas de Viagem", listen: "Ouvir", listenAll: "Ouvir tudo",
    sectionTitle: "Ouça com Maya",
    premiumTitle: "Descubra este destino com Maya",
    welcome: "Olá, sou a Maya. Quer conhecer a história, cultura, gastronomia e curiosidades deste destino antes de reservar?",
    pause: "Pausar", resume: "Retomar", stop: "Parar", play: "Reproduzir",
    loading: "Maya está preparando a narração…",
    fallback: "Ainda não tenho narração para este destino, mas terei em breve.",
    error: "Maya não pôde narrar agora. Tente novamente em alguns segundos.",
    tooMany: "Muitas solicitações. Aguarde um momento e tente novamente.",
    accessRequired: "Você precisa do acesso Maya Premium para ouvir. Desbloqueie abaixo.",
    nowPlaying: "Reproduzindo",
    selectTopic: "Escolha um tema e deixe a voz da Maya te guiar.",
  },
  it: {
    history: "Storia", faunaFlora: "Flora e Fauna", geography: "Geografia",
    culture: "Cultura e Tradizioni", gastronomy: "Gastronomia",
    archaeology: "Archeologia e Patrimonio", legends: "Leggende e Curiosità",
    tips: "Consigli di Viaggio", listen: "Ascolta", listenAll: "Ascolta tutto",
    sectionTitle: "Ascolta con Maya",
    premiumTitle: "Scopri questa destinazione con Maya",
    welcome: "Ciao, sono Maya. Vuoi conoscere la storia, la cultura, la gastronomia e le curiosità di questa destinazione prima di prenotare?",
    pause: "Pausa", resume: "Riprendi", stop: "Ferma", play: "Riproduci",
    loading: "Maya sta preparando la narrazione…",
    fallback: "Non ho ancora la narrazione per questa destinazione, sarà pronta presto.",
    error: "Maya non è riuscita a narrare ora. Riprova tra qualche secondo.",
    tooMany: "Troppe richieste. Attendi un momento e riprova.",
    accessRequired: "Serve l'accesso Maya Premium per ascoltare. Sbloccalo qui sotto.",
    nowPlaying: "In riproduzione",
    selectTopic: "Scegli un tema e lascia che la voce di Maya ti guidi.",
  },
} as const;

function pickLang(lang: string): SupportedLang {
  const l = normalizeLang(lang);
  if (l === "en" || l === "fr" || l === "pt" || l === "it") return l;
  return "es";
}

function buildTopicText(
  topic: TopicDef,
  guide: DestinationGuide,
  destination: string,
  lang: SupportedLang,
): string {
  const intro = `${topic.emoji} ${TOPIC_LABELS[lang][topic.labelKey]} — ${destination}.`;
  if (topic.travelTips) {
    const tips = guide.didYouKnow.slice(0, 4);
    const lead = ({
      es: `Antes de visitar ${destination}, ten en cuenta algunos consejos prácticos de viaje:`,
      en: `Before visiting ${destination}, keep these practical travel tips in mind:`,
      fr: `Avant de visiter ${destination}, garde en tête ces conseils pratiques :`,
      pt: `Antes de visitar ${destination}, lembre-se destas dicas práticas:`,
      it: `Prima di visitare ${destination}, tieni a mente questi consigli pratici:`,
    } as const)[lang];
    return [intro, lead, ...tips.map((d) => `• ${d}`), guide.intro].filter(Boolean).join("\n\n");
  }
  const parts = guide.sections
    .filter((s) => topic.sectionKeys.includes(s.key))
    .map((s) => `${s.title}. ${s.body}`);
  if (topic.includeDidYouKnow && guide.didYouKnow.length) {
    parts.push(...guide.didYouKnow.slice(0, 4).map((d) => `• ${d}`));
  }
  if (!parts.length) return `${intro}\n\n${guide.intro}`;
  return [intro, ...parts].join("\n\n");
}

function buildFullText(guide: DestinationGuide, destination: string, lang: SupportedLang): string {
  const header = ({
    es: `Hola, soy Maya. Te invito a descubrir ${destination}.`,
    en: `Hi, I'm Maya. Let's discover ${destination} together.`,
    fr: `Bonjour, je suis Maya. Découvrons ensemble ${destination}.`,
    pt: `Olá, sou a Maya. Vamos descobrir ${destination} juntos.`,
    it: `Ciao, sono Maya. Scopriamo insieme ${destination}.`,
  } as const)[lang];
  const sections = guide.sections.map((s) => `${s.title}. ${s.body}`).join("\n\n");
  const dykHeading = ({ es: "¿Sabías que…?", en: "Did you know…?", fr: "Le savais-tu…?", pt: "Você sabia…?", it: "Lo sapevi…?" } as const)[lang];
  const dyk = guide.didYouKnow.length
    ? "\n\n" + dykHeading + "\n" + guide.didYouKnow.map((d) => `• ${d}`).join("\n")
    : "";
  return [header, guide.intro, sections + dyk].filter(Boolean).join("\n\n").slice(0, 3400);
}

export function MayaAudioGuide({ title, location, summary, tourSlug = null, variant = "premium", itinerary }: Props) {
  const places = itinerary ? detectTourPlaces({ title, itinerary }).places.map((p) => p.name) : undefined;
  const { i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const labels = TOPIC_LABELS[lang];

  const destinationKey = (location || title || "").toLowerCase().trim();


  // Maya Premium access gate — paywall above the controls when not unlocked.
  const { session } = useAuth();
  const userEmail = session?.user?.email ?? null;
  const userId = session?.user?.id ?? null;
  const guestEmail = userEmail ?? getRememberedMayaEmail();
  const qc = useQueryClient();

  const published = usePublishedGuide(tourSlug);
  const { data: access } = useQuery({
    queryKey: ["maya-access", destinationKey, tourSlug ?? "", userId ?? "guest", guestEmail],
    queryFn: async () => {
      if (userId) {
        try {
          const r = await getMyMayaAccess({ data: { destinationKey, tourSlug: tourSlug ?? undefined } });
          return {
            hasPass: r.hasPass || r.isUnlimited || r.isOwner,
            hasDestination: r.hasAccess,
            isOwner: r.isOwner,
          };
        } catch {
          /* fall through to guest path */
        }
      }
      if (guestEmail) {
        const r = await getGuestMayaAccess({
          data: { destinationKey, tourSlug: tourSlug ?? undefined, email: guestEmail },
        });
        return {
          hasPass: r.hasPass || r.isUnlimited,
          hasDestination: r.hasAccess,
          isOwner: false,
        };
      }
      return { hasPass: false, hasDestination: false, isOwner: false };
    },
    enabled: !!destinationKey && published,
    staleTime: 60_000,
  });
  const unlocked = !!access && (access.hasPass || access.hasDestination);

  // COSTE IA: la guía solo se genera cuando el viajero ya tiene acceso premium.
  // Antes se generaba en cada visita a la ficha del tour, incluso sin comprar.
  const { data, isLoading: guideLoading } = useQuery({
    queryKey: ["destination-guide", lang, title, location, places?.join("|") ?? ""],
    queryFn: () =>
      getDestinationGuide({ data: { title, location, summary, lang, places, generate: true } }),
    enabled: unlocked,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });

  const guide = data?.guide ?? null;
  const destination = guide?.destination ?? location ?? title;



  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [loadingTopic, setLoadingTopic] = useState<string | null>(null);
  const [downloadingTopic, setDownloadingTopic] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const DOWNLOAD_LABEL: Record<SupportedLang, string> = {
    es: "Descargar audio",
    en: "Download audio",
    fr: "Télécharger l'audio",
    pt: "Baixar áudio",
    it: "Scarica audio",
  };

  async function downloadTopic(topic: TopicDef | "all") {
    if (!guide) return;
    if (!unlocked) return;
    setError(null);
    const topicId = topic === "all" ? "all" : topic.id;
    const text =
      topic === "all"
        ? buildFullText(guide, destination, lang)
        : buildTopicText(topic, guide, destination, lang);
    setDownloadingTopic(topicId);
    try {
      const { data: sessionData } = await (await getSupabase()).auth.getSession();
      const accessToken = sessionData.session?.access_token ?? null;
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
      const resp = await fetch("/api/maya-tts", {
        method: "POST",
        headers,
        body: JSON.stringify({
          text,
          lang,
          provider: "lovable",
          destinationKey,
          ...(tourSlug ? { tourSlug } : {}),
          mode: "download",
          deviceId: getMayaAnonSessionId() || undefined,
          ...(guestEmail && !accessToken ? { email: guestEmail } : {}),
        }),
      });

      if (resp.status === 403) {
        qc.invalidateQueries({ queryKey: ["maya-access"] });
        setError(labels.accessRequired);
        return;
      }
      if (resp.status === 429) {
        setError(labels.tooMany);
        return;
      }
      if (!resp.ok) throw new Error(`TTS ${resp.status}`);
      const blob = await resp.blob();
      const ext = (blob.type.includes("mpeg") || blob.type.includes("mp3")) ? "mp3"
        : blob.type.includes("wav") ? "wav"
        : blob.type.includes("ogg") ? "ogg" : "mp3";
      const safeDest = destination.replace(/[^\p{L}\p{N}]+/gu, "-").toLowerCase();
      const filename = `maya-${safeDest}-${topicId}-${lang}.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      console.warn("[maya-audio] download", err);
      setError(labels.error);
    } finally {
      setDownloadingTopic(null);
    }
  }


  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, []);

  function resetAudio() {
    abortRef.current?.abort();
    abortRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setIsPlaying(false);
  }

  async function playTopic(topic: TopicDef | "all") {
    if (!guide) return;
    if (!unlocked) return; // UX gate — endpoint enforces the real check
    setError(null);
    const topicId = topic === "all" ? "all" : topic.id;
    const text =
      topic === "all"
        ? buildFullText(guide, destination, lang)
        : buildTopicText(topic, guide, destination, lang);

    resetAudio();
    setActiveTopic(topicId);
    setLoadingTopic(topicId);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Attach bearer token when signed in so the endpoint's has_maya_access
      // check resolves the user by verified `sub`, not just by client-supplied
      // email.
      const { data: sessionData } = await (await getSupabase()).auth.getSession();
      const accessToken = sessionData.session?.access_token ?? null;
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

      const resp = await fetch("/api/maya-tts", {
        method: "POST",
        headers,
        body: JSON.stringify({
          text,
          lang,
          provider: "lovable",
          destinationKey,
          ...(tourSlug ? { tourSlug } : {}),
          mode: "stream",
          deviceId: getMayaAnonSessionId() || undefined,
          ...(guestEmail && !accessToken ? { email: guestEmail } : {}),
        }),
        signal: controller.signal,
      });

      if (resp.status === 403) {
        // Access revoked or never valid — reopen paywall and drop cached unlock.
        qc.invalidateQueries({ queryKey: ["maya-access"] });
        setError(labels.accessRequired);
        setActiveTopic(null);
        return;
      }
      if (resp.status === 429) {
        setError(labels.tooMany);
        setActiveTopic(null);
        return;
      }
      if (!resp.ok) throw new Error(`TTS ${resp.status}`);
      const blob = await resp.blob();
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = audioRef.current ?? new Audio();
      audioRef.current = audio;
      audio.src = url;
      audio.onended = () => setIsPlaying(false);
      audio.onpause = () => setIsPlaying(false);
      audio.onplay = () => setIsPlaying(true);
      await audio.play();
    } catch (err) {
      if (controller.signal.aborted) return;
      console.warn("[maya-audio]", err);
      setError(labels.error);
      setActiveTopic(null);
    } finally {
      if (abortRef.current === controller) {
        setLoadingTopic(null);
      }
    }
  }

  function togglePause() {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => undefined);
    else a.pause();
  }

  function stop() {
    resetAudio();
    setActiveTopic(null);
  }

  const hasGuide = !!guide && (guide.sections.length > 0 || guide.didYouKnow.length > 0);
  const isPremium = variant === "premium";

  // Sin audioguía real publicada para este tour, el bloque no se muestra.
  if (!published || !tourSlug) return null;

  return (
    <section
      aria-label={labels.premiumTitle}
      className={cn(
        "relative overflow-hidden rounded-3xl border shadow-sm",
        isPremium
          ? "border-primary/30 bg-gradient-to-br from-primary/10 via-card to-accent/10 p-6 sm:p-8"
          : "border-border bg-card p-5",
      )}
    >
      <div className="pointer-events-none absolute -right-12 -top-12 hidden h-48 w-48 rounded-full bg-primary/10 blur-3xl sm:block" aria-hidden="true" />

      <header className="relative flex flex-wrap items-start gap-3">
        <MayaMark size={48} />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
            Maya · {labels.sectionTitle}
          </div>
          <h2 className="mt-1 font-display text-xl font-semibold leading-tight sm:text-2xl">
            {labels.premiumTitle}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{labels.welcome}</p>
          <Link
            to="/audioguia/$tourSlug"
            params={{ tourSlug }}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Play className="h-4 w-4" /> {FIRST_CHAPTER_FREE[i18n.language.slice(0, 2)] ?? FIRST_CHAPTER_FREE.es}
          </Link>
        </div>
      </header>

      {!unlocked && destinationKey && (
        <>
          <MayaAudioPreview
            destinationKey={destinationKey}
            destinationName={destination}
            location={location}
            summary={summary}
            lang={(lang === "it" ? "es" : lang) as "es" | "en" | "fr" | "pt"}
          />
          <MayaPaywall
            destinationKey={destinationKey}
            tourSlug={tourSlug ?? null}
            customerEmail={guestEmail}
            userId={userId}
            lang={i18n.language.slice(0, 2) === "de" ? "de" : lang}
          />
        </>
      )}

      {unlocked && (
        <div className="relative mt-5">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {labels.sectionTitle}
          </div>

          {guideLoading && !guide ? (
            <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {labels.loading}
            </p>
          ) : !hasGuide ? null : (
            <>
              <p className="mb-3 text-xs text-muted-foreground">{labels.selectTopic}</p>
              <div className="flex flex-wrap gap-2">
                {TOPICS.map((topic) => {
                  const active = activeTopic === topic.id;
                  const loading = loadingTopic === topic.id;
                  return (
                    <button
                      key={topic.id}
                      type="button"
                      onClick={() => playTopic(topic)}
                      disabled={loading}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                        active
                          ? "border-primary bg-primary text-primary-foreground shadow"
                          : "border-border bg-background hover:border-primary/40 hover:bg-primary/5",
                      )}
                    >
                      {loading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <span>{topic.emoji}</span>
                      )}
                      {labels[topic.labelKey]}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => playTopic("all")}
                  disabled={loadingTopic === "all"}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/20"
                >
                  {loadingTopic === "all" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5" />
                  )}
                  {labels.listenAll}
                </button>
                <button
                  type="button"
                  onClick={() => downloadTopic("all")}
                  disabled={downloadingTopic === "all"}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-background px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-60"
                  title={DOWNLOAD_LABEL[lang]}
                >
                  {downloadingTopic === "all" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5" />
                  )}
                  {DOWNLOAD_LABEL[lang]}
                </button>
              </div>

              {activeTopic && (
                <div className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-background/60 p-3">
                  <button
                    type="button"
                    onClick={togglePause}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground"
                    aria-label={isPlaying ? labels.pause : labels.resume}
                  >
                    {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={stop}
                    className="inline-flex h-9 items-center gap-1 rounded-full border border-border px-3 text-xs font-semibold text-muted-foreground hover:text-foreground"
                    aria-label={labels.stop}
                  >
                    <Square className="h-3.5 w-3.5" /> {labels.stop}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {labels.nowPlaying}: {activeTopic === "all" ? labels.listenAll : labels[TOPICS.find((t) => t.id === activeTopic)?.labelKey ?? "history"]}
                  </span>
                </div>
              )}

              {error && (
                <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                  {error}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
